import {
    setEventDetailsQuery,
    useBusinessInfo,
    useEventDetailsQuery,
} from '../../lib/Queries';
import { useEffect, useMemo, useRef, useState } from 'react';
import useBusinessPostApi from '../../lib/useBusinessPostApi';
import * as Toast from '../../lib/Toast';
import SidePanel from '../SidePanel';
import SectionHeading from '../SectionHeading';
import ClipboardText from '../icons/ClipboardText';
import DotDotDotMenu from '../DotDotDotMenu';
import EventDateAndTime from './EventDateAndTime';
import CheckCircle2Icon from '../icons/CheckCircle2Icon';
import CalendarMarkIcon from '../icons/CalendarMarkIcon';
import ReschedulingPanel from '../scheduling/ReschedulingPanel';
import AuditLogs from '../AuditLogs';
import UserCircleSingleIcon from '../icons/UserCircleSingleIcon';
import ClientDetails from '../clients/ClientDetails';
import Payments from '../scheduling/Payments';
import {
    ArchiveBoxIcon,
    PencilSquareIcon,
} from '@heroicons/react/24/outline/index';
import PendingUntilBanner from './PendingUntilBanner';
import ClientPackageGrants from '../clients/ClientPackageGrants';
import EventLocation from './EventLocation';
import AssignPackagePanel from '../packages/AssignPackagePanel';
import { ObjectTypes } from '../../lib/ObjectTypes';
import CustomFields from '../customfields/CustomFields';
import CustomTags from '../customfields/CustomTags';
import EventFormAnswers from './EventFormAnswers';
import { deepCopy } from '~/lib/Utils';
import { formatCurrency } from '~/lib/CurrencyHelpers';
import MoneyInput from '~/components/MoneyInput';
import InfoMessageBlock from '~/components/InfoMessageBlock';
import NudgeMessageBlock from '~/components/NudgeMessageBlock';
import MergeIcon from '~/components/icons/MergeIcon';
import EventChangeClientModal from '~/components/calendar/EventChangeClientModal';
import useDelete from '~/lib/useDelete';
import { ArrowLeftIcon, CalendarDaysIcon } from '@heroicons/react/24/outline/index.js';
import CancelEventModal from '~/components/scheduling/CancelEventModal';
import ErrorMessageBlock from '~/components/ErrorMessageBlock';
import EventTypeSelectDropdown from '~/components/EventTypeSelectDropdown';
import { differenceInMinutes } from 'date-fns';
import usePermissions from '~/lib/usePermissions';

type EventDetailsPanelProps = {
    event?: { id: number };
    isOpen: boolean;
    onClose: () => void;
    onEventChanged?: () => void;
    onBookAgain?: (client: any, eventTypeId: number) => void;
    justScheduled?: boolean;
};

const EventDetailsPanel = ({
    event,
    isOpen,
    onClose,
    onEventChanged,
    onBookAgain,
    justScheduled,
}: EventDetailsPanelProps) => {
    const [displayEvent, setDisplayEvent] = useState(event);
    const [isRescheduling, setIsRescheduling] = useState(false);
    const [showClient, setShowClient] = useState(null);
    const [showChangeClient, setShowChangeClient] = useState(false);
    const [showAssignPackage, setShowAssignPackage] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingData, setEditingData] = useState({});
    const [showCancel, setShowCancel] = useState(false);

    const panelRef = useRef();
    const packageRef = useRef();
    const paymentsRef = useRef();

    const { data, isLoading, refetch, error } = useEventDetailsQuery(
        displayEvent?.id,
    );

    const isAccessDenied = error?.status === 403;

    const { save, isSaving } = useBusinessPostApi(
        `/calendar/events/${event?.id}`,
    );
    const { data: businessData } = useBusinessInfo();
    const { can } = usePermissions();
    const canUsePackages = can('packages');

    const { save: saveUnancel, isSaving: isUncanceling } = useBusinessPostApi(
        `/calendar/events/${event?.id}/uncancel`,
    );

    const { saveDelete, isDeleting } = useDelete(
        `/calendar/events/${event?.id}/delete`,
    );

    async function doRefetch() {
        const result = await refetch();
        if (result.data?.event) {
            setDisplayEvent(result.data?.event);
        }
    }

    function doUncancel() {
        saveUnancel().then(result => {
            // Don't close this, just so they can refund if needed
            if (typeof onEventChanged === 'function') {
                onEventChanged();
                doRefetch();
            }
            Toast.success(
                result?.message || 'Successfully uncanceled appointment',
            );
        });
    }

    function doDelete() {
        saveDelete().then(() => {
            Toast.success('Successfully deleted appointment');
            onClose();
            if (typeof onEventChanged === 'function') {
                onEventChanged();
            }
        });
    }

    function doSave() {
        save(editingData).then(result => {
            Toast.success('Successfully saved');
            setDisplayEvent(result.event);
            setIsEditing(false);
            if (typeof onEventChanged === 'function') {
                onEventChanged();
            }
        });
    }

    function updateEvent(result) {
        setEventDetailsQuery(event.id, result);
        setDisplayEvent(result.event);
    }

    const { form, answers, submission, isClientNameMismatch } = data || {};

    useEffect(() => {
        if (event) {
            if (
                !data?.event ||
                parseInt(event.id) === parseInt(data.event.id)
            ) {
                const newEvent = data?.event || event;
                setEditingData(deepCopy(newEvent));
                setDisplayEvent(newEvent);
            } else {
                setEditingData(deepCopy(event));
                setDisplayEvent(event);
            }
        } else {
            // It's disappearing, wait a little bit so the modal stays rendered
            //  and the animation fades
            setTimeout(() => {
                setDisplayEvent(null);
            }, 300);
        }
    }, [event?.id, data]);

    useEffect(() => {
        panelRef.current?.closeAllAbove();
        if (
            displayEvent?.id &&
            (event?.id !== displayEvent?.id || displayEvent?.client_id == null)
        ) {
            doRefetch();
        }
    }, [JSON.stringify(event?.id)]);

    const isCanceled = !!displayEvent?.canceled_at;

    // Admins schedule without going through checkout, so nudge them to collect
    //  the money for an appointment they just booked
    const nudgeForPayment =
        justScheduled &&
        !isCanceled &&
        displayEvent?.price > 0 &&
        !!businessData?.canPay;

    const isNearEvent = useMemo(() => {
        if (!displayEvent?.start && !displayEvent?.client_start) return false;
        const now = new Date();
        const start = new Date(displayEvent.start || displayEvent.client_start);
        const end = new Date(displayEvent.end || displayEvent.client_end);
        const minutesToStart = Math.abs(differenceInMinutes(now, start));
        const minutesToEnd = Math.abs(differenceInMinutes(now, end));
        return minutesToStart <= 60 || minutesToEnd <= 60;
    }, [displayEvent?.start, displayEvent?.end, displayEvent?.client_start, displayEvent?.client_end]);

    const menuOptions = [];

    if (submission?.confirmation_url) {
        menuOptions.push({
            name: 'Confirmation Page',
            onClick: () => {
                window.open(submission?.confirmation_url, '_blank').focus();
            },
            icon: <CheckCircle2Icon />,
        });
    }

    menuOptions.push({
        name: 'Client Details',
        onClick: () => {
            setShowClient(displayEvent.client);
            return true;
        },
        icon: <UserCircleSingleIcon />,
    });

    if (businessData?.hasPackages && canUsePackages) {
        menuOptions.push({
            name: 'Use Package',
            onClick: () => {
                setShowAssignPackage(true);
                return true;
            },
            icon: <ArchiveBoxIcon />,
        });
    }

    menuOptions.push({
        name: 'Edit',
        onClick: () => {
            setIsEditing(true);
            return true;
        },
        icon: <PencilSquareIcon />,
    });

    menuOptions.push({
        name: 'Change Client',
        onClick: () => {
            setShowChangeClient(true);
            return true;
        },
        icon: <MergeIcon />,
    });

    if (!isCanceled) {
        //if (!displayEvent?.event_type?.is_series) {
        menuOptions.push({
            name: 'Reschedule',
            onClick: () => setIsRescheduling(true),
            icon: <CalendarMarkIcon />,
        });
        //}

        menuOptions.push({
            name: 'Cancel',
            isDelete: true,
            onClick: () => setShowCancel(true),
        });
    } else {
        menuOptions.push({
            name: isUncanceling ? 'Undoing cancel...' : 'Undo Cancel',
            onClick: doUncancel,
            icon: <ArrowLeftIcon />,
        });

        menuOptions.push({
            name: isDeleting ? 'Deleting...' : 'Delete',
            isDelete: true,
            onClick: doDelete,
        });
    }

    return (
        <SidePanel ref={panelRef} isOpen={isOpen} onClose={onClose} title=" ">
            {isAccessDenied && (
                <ErrorMessageBlock className="mb-4">
                    You don't have access to this appointment.
                </ErrorMessageBlock>
            )}
            {!!displayEvent && !isAccessDenied && (
                <>
                    {!!displayEvent.pending_until && (
                        <PendingUntilBanner
                            pendingUntil={displayEvent.pending_until}
                        />
                    )}

                    {(nudgeForPayment) && (
                        <NudgeMessageBlock className="mb-6">
                            <div className="mb-2">
                                This was booked by an admin and costs {formatCurrency(displayEvent.price)}
                            </div>
                            <button
                                type="button"
                                className="underline"
                                onClick={() =>
                                    paymentsRef.current?.makePayment()
                                }
                            >
                                Make a Payment
                            </button>
                        </NudgeMessageBlock>
                    )}
                    <div className="flex items-center mb-4">
                        <div className="flex-1">
                            <h2>{displayEvent.name}</h2>
                            <div className="text-gray-500">
                                {displayEvent.event_type?.name} with{' '}
                                {displayEvent.bookable_resource?.name}
                                {!!displayEvent.bookable_resource?.deleted_at && (
                                    <span className="tag bg-red-500 ml-2">Deleted</span>
                                )}
                            </div>
                        </div>
                        <div>
                            <DotDotDotMenu
                                btnClassName="btn-light-circle"
                                options={menuOptions}
                            />
                        </div>
                    </div>

                    {isNearEvent && !isCanceled && onBookAgain && (
                        <InfoMessageBlock className="mb-6">
                            <div className="mb-2">Schedule another event for this client with the same event type</div>
                            <button
                                type="button"
                                className="btn-pill"
                                onClick={() => {
                                    onBookAgain(
                                        displayEvent.client,
                                        displayEvent.event_type_id,
                                    );
                                }}
                            >
                                Book Again
                            </button>
                        </InfoMessageBlock>
                    )}

                    <CustomTags
                        type={ObjectTypes.CLIENT}
                        id={displayEvent.client?.id}
                        fields={displayEvent.client?.custom_fields}
                        className="mb-8"
                    />

                    {displayEvent.client_start && (
                        <EventDateAndTime event={displayEvent} />
                    )}

                    {isEditing && (
                        <div className="mb-4">
                            {!displayEvent.event_type?.is_class && (
                                <EventTypeSelectDropdown
                                    value={editingData.event_type_id}
                                    onChange={value =>
                                        setEditingData(old => ({
                                            ...old,
                                            event_type_id: value,
                                        }))
                                    }
                                    className="mb-4"
                                />
                            )}

                            <MoneyInput
                                autoFocus={true}
                                label="Price"
                                value={editingData.price}
                                onChange={value =>
                                    setEditingData(old => ({
                                        ...old,
                                        price: value,
                                    }))
                                }
                                className="mb-4"
                            />
                            <div className="flex items-center space-x-6">
                                <button
                                    type="button"
                                    className="btn-save"
                                    onClick={doSave}
                                >
                                    {isSaving ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                    type="button"
                                    className="btn-gray-pill-lg"
                                    onClick={() => setIsEditing(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {!isEditing && (
                        <>
                            <EventLocation
                                event={displayEvent}
                                updateEvent={updateEvent}
                            />

                            <CustomFields
                                type={ObjectTypes.BOOKING_EVENT}
                                id={event?.id}
                                fields={displayEvent?.custom_fields}
                                onChanged={onEventChanged}
                                className="mt-4"
                            />

                            {isClientNameMismatch && (
                                <InfoMessageBlock
                                    showIcon={true}
                                    className="mb-4"
                                >
                                    The client's name in the form answers below
                                    is different than the name of the client
                                    this appointment is associated with.
                                </InfoMessageBlock>
                            )}
                        </>
                    )}

                    {!!answers && (
                        <EventFormAnswers
                            key={event?.id}
                            answers={answers}
                            event={event}
                            refetch={() => {
                                doRefetch()
                                paymentsRef.current?.refetch();
                            }}
                        />
                    )}

                    {canUsePackages && (
                        <ClientPackageGrants
                            search={{ eventId: displayEvent?.id }}
                            onEditPackages={() => setShowAssignPackage(true)}
                            className="mt-8"
                            ref={packageRef}
                        />
                    )}

                    <Payments
                        key={`payments-${displayEvent?.id}-${displayEvent?.price}`}
                        event={displayEvent}
                        headingClassName="mt-8"
                        refetch={doRefetch}
                        ref={paymentsRef}
                    />

                    <SectionHeading
                        icon={<ClipboardText />}
                        name="Logs"
                        className="mt-8"
                    />
                    <AuditLogs
                        type={ObjectTypes.BOOKING_EVENT}
                        id={event?.id}
                        className="mt-2"
                    />

                    <ReschedulingPanel
                        isOpen={isRescheduling}
                        onClose={() => setIsRescheduling(false)}
                        onRescheduled={() => {
                            doRefetch();
                            if (typeof onEventChanged === 'function') {
                                onEventChanged();
                            }
                        }}
                        event={event}
                    />

                    <ClientDetails
                        client={showClient}
                        onClose={() => setShowClient(null)}
                        refetch={() => null}
                    />

                    {canUsePackages && (
                        <AssignPackagePanel
                            event={event}
                            isOpen={showAssignPackage}
                            onClose={() => setShowAssignPackage(false)}
                            refetch={() => {
                                packageRef.current?.refetch();
                                paymentsRef.current?.refetch();
                                doRefetch();
                            }}
                        />
                    )}

                    <EventChangeClientModal
                        event={displayEvent}
                        isOpen={showChangeClient}
                        onClose={() => setShowChangeClient(false)}
                        onSave={doRefetch}
                    />

                    <CancelEventModal
                        isOpen={showCancel}
                        onClose={() => setShowCancel(false)}
                        event={displayEvent}
                        onCancelEvent={() => {
                            if (typeof onEventChanged === 'function') {
                                onEventChanged();
                            }
                            doRefetch();
                        }}
                    />
                </>
            )}
        </SidePanel>
    );
};

export default EventDetailsPanel;
